/**
 * Offline Queue — IndexedDB-backed retry queue for uploads that happen
 * on flaky field connections (basements, elevators, parking garages).
 *
 * Scope (per user pick): everything a tech does inside a lightbox or on
 * the job view — photo uploads, voice-note uploads, caption PATCHes,
 * markup uploads. Anything that took a real request originally and
 * would otherwise be lost the moment the browser closes.
 *
 * Retry policy (per user pick): keep forever + keep showing the pill.
 * The queue does NOT auto-drop failed items. A failed item stays with
 * an incremented attempt counter and gets retried on the next drain.
 *
 * Layout on disk (`objectStore='requests'`):
 *   id (uuid)     — auto-generated primary key
 *   created_at    — ISO string
 *   kind          — 'photo' | 'voice_note' | 'caption' | 'markup'
 *   endpoint      — API path relative (e.g. '/jobs/xxx/photo')
 *   method        — 'POST' | 'PATCH'
 *   is_multipart  — bool; if true, `blob` + `blob_type` + `filename` + `form_fields` describe FormData
 *                    if false, `json` carries the JSON body
 *   blob          — Blob for multipart
 *   blob_type     — mime of blob
 *   filename      — original filename
 *   form_fields   — dict<string,string> of extra fields (e.g. kind='before')
 *   json          — parsed object for JSON PATCH endpoints
 *   attempts      — int
 *   last_error    — last error message (for the tray in a future version)
 *   token         — bearer to send back (captured at enqueue time so a
 *                    later session with a new token still uploads)
 *
 * Not a service worker — we only need the tab-open case (techs keep the
 * WO open while working). A true SW/background sync is a future add.
 */

const DB_NAME = "fieldradar-offline";
const DB_VERSION = 1;
const STORE = "requests";

// Simple event bus so React components can subscribe to queue changes
const listeners = new Set();
function emit() {
  listeners.forEach(fn => { try { fn(); } catch {} });
}
export function subscribeQueue(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const st = db.createObjectStore(STORE, { keyPath: "id" });
        st.createIndex("created_at", "created_at", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode = "readonly") {
  return db.transaction(STORE, mode).objectStore(STORE);
}

function uuid() {
  // RFC-4122-ish; enough uniqueness for a local queue
  if (crypto?.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

export async function enqueue(item) {
  const db = await openDb();
  const record = {
    id: uuid(),
    created_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
    ...item,
  };
  await new Promise((resolve, reject) => {
    const r = tx(db, "readwrite").add(record);
    r.onsuccess = () => resolve();
    r.onerror = () => reject(r.error);
  });
  emit();
  // Try immediate drain — if we're actually online this uploads right away.
  drainQueue();
  return record.id;
}

export async function getAll() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const r = tx(db).getAll();
    r.onsuccess = () => resolve(r.result || []);
    r.onerror = () => reject(r.error);
  });
}

export async function remove(id) {
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const r = tx(db, "readwrite").delete(id);
    r.onsuccess = () => resolve();
    r.onerror = () => reject(r.error);
  });
  emit();
}

export async function updateAttempts(id, attempts, lastError = null) {
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const st = tx(db, "readwrite");
    const g = st.get(id);
    g.onsuccess = () => {
      const rec = g.result;
      if (!rec) return resolve();
      rec.attempts = attempts;
      rec.last_error = lastError;
      const p = st.put(rec);
      p.onsuccess = () => resolve();
      p.onerror = () => reject(p.error);
    };
    g.onerror = () => reject(g.error);
  });
  emit();
}

// ── Drain ─────────────────────────────────────────────────────────
// Runs items in oldest-first order, one at a time. Keeps failing items
// in the queue (per user pick) and moves on to the next. Any item that
// actually succeeds gets deleted.
let draining = false;
export async function drainQueue({ apiBase = "" } = {}) {
  if (draining) return { drained: 0, kept: 0 };
  if (!navigator.onLine) return { drained: 0, kept: 0 };
  draining = true;
  const base = (apiBase || process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  let drained = 0, kept = 0;
  try {
    const items = await getAll();
    items.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    for (const it of items) {
      try {
        await sendOne(it, base);
        await remove(it.id);
        drained++;
      } catch (err) {
        // Keep the item — bump attempt counter, log last error, move on.
        await updateAttempts(it.id, (it.attempts || 0) + 1, String(err?.message || err || "unknown"));
        kept++;
      }
    }
  } finally {
    draining = false;
    emit();
  }
  return { drained, kept };
}

async function sendOne(it, base) {
  const url = `${base}/api${it.endpoint}`;
  const headers = {};
  if (it.token) headers.Authorization = `Bearer ${it.token}`;
  let body;
  if (it.is_multipart) {
    if (!it.blob) throw new Error("Missing blob for multipart request");
    const fd = new FormData();
    Object.entries(it.form_fields || {}).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", new File([it.blob], it.filename || "upload.bin", { type: it.blob_type || "application/octet-stream" }));
    body = fd;
  } else {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(it.json || {});
  }
  const res = await fetch(url, { method: it.method || "POST", headers, body });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    // 4xx that's user-fixable (401/403 auth, 404 gone job) — still keep,
    // since the user picked "keep forever". A future settings toggle can
    // flip to auto-drop.
    throw new Error(`HTTP ${res.status} ${text.slice(0, 120)}`);
  }
}

// ── Auto-drain triggers ──────────────────────────────────────────
// Fire when we regain network, on tab focus, and every 30s while a
// drain isn't in progress.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => drainQueue());
  window.addEventListener("focus", () => drainQueue());
  setInterval(() => drainQueue(), 30_000);
}

// ── Convenience shape helpers ────────────────────────────────────
export function makePhotoUpload({ jobId, kind, blob, filename, token, room = null }) {
  const fields = { kind };
  if (room) fields.room = room;
  return {
    kind: "photo",
    endpoint: `/jobs/${jobId}/photo`,
    method: "POST",
    is_multipart: true,
    blob,
    blob_type: blob.type || "image/jpeg",
    filename: filename || `photo-${Date.now()}.jpg`,
    form_fields: fields,
    token,
  };
}

export function makeVoiceNoteUpload({ jobId, fileId, blob, token }) {
  return {
    kind: "voice_note",
    endpoint: `/jobs/${jobId}/photo/${fileId}/voice-note`,
    method: "POST",
    is_multipart: true,
    blob,
    blob_type: blob.type || "audio/webm",
    filename: `voice-${Date.now()}.webm`,
    form_fields: {},
    token,
  };
}

export function makeCaptionUpdate({ jobId, fileId, caption, token }) {
  return {
    kind: "caption",
    endpoint: `/jobs/${jobId}/photo/${fileId}/caption`,
    method: "PATCH",
    is_multipart: false,
    json: { caption },
    token,
  };
}

export function makeMarkupUpload({ jobId, blob, token }) {
  return {
    kind: "markup",
    endpoint: `/jobs/${jobId}/photo`,
    method: "POST",
    is_multipart: true,
    blob,
    blob_type: blob.type || "image/jpeg",
    filename: `markup-${Date.now()}.jpg`,
    form_fields: { kind: "annotated" },
    token,
  };
}

// Convenience wrapper that tries a live upload first and falls back to
// enqueue on network failure. Live upload uses fetch directly so we get
// consistent error semantics with the drain path.
export async function tryLiveOrQueue(itemBuilder, token) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const item = itemBuilder({ token });
  try {
    await sendOne(item, base);
    return { queued: false };
  } catch (err) {
    // Only queue on true network failures — TypeError from fetch, or 5xx.
    // 4xx (bad request, auth) surface immediately so the user sees them.
    const msg = String(err?.message || err || "");
    const looksNetwork = err?.name === "TypeError"
      || /Failed to fetch|NetworkError|ERR_NETWORK|timeout|HTTP 5\d\d/.test(msg)
      || !navigator.onLine;
    if (looksNetwork) {
      await enqueue(item);
      return { queued: true, error: msg };
    }
    throw err;
  }
}

// Handy for the pill
export async function queueCount() {
  const all = await getAll();
  return all.length;
}
